import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, ShieldCheck, Clock, ArrowRight, Check, FileText, Loader2, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiErrorDetail } from "@/lib/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUNDESLAND_OPTIONS = [
  "Nordrhein-Westfalen", "Bayern", "Baden-Württemberg", "Niedersachsen", "Hessen",
  "Rheinland-Pfalz", "Sachsen", "Berlin", "Schleswig-Holstein", "Brandenburg",
  "Sachsen-Anhalt", "Thüringen", "Hamburg", "Mecklenburg-Vorpommern", "Saarland", "Bremen",
];

// ─────────────────────────────────────────────────────────────────
// LANDING PAGE
// ─────────────────────────────────────────────────────────────────
export default function PflegeKiTesten() {
  const [sp] = useSearchParams();
  const cancelled = sp.get("cancelled") === "1";
  const utmSource = sp.get("utm_source") || "";
  const utmCampaign = sp.get("utm_campaign") || "";
  const firmaPrefill = sp.get("firma") || "";

  const [form, setForm] = useState({
    firmenname: firmaPrefill,
    name: "",
    email: "",
    phone: "",
    bundesland: "",
    anzahl_mitarbeiter: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (cancelled) {
      toast.info("Bezahlung abgebrochen. Du kannst es jederzeit erneut versuchen.");
    }
  }, [cancelled]);

  const canSubmit =
    form.firmenname.trim().length >= 2 &&
    form.name.trim().length >= 2 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) &&
    form.phone.trim().length >= 5 &&
    form.bundesland.trim().length >= 2;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/schnupper/checkout`, {
        ...form,
        email: form.email.trim().toLowerCase(),
        origin_url: window.location.origin,
        utm_source: utmSource,
        utm_campaign: utmCampaign,
      });
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Checkout fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  return (
    <div className="min-h-screen bg-cream-50" data-testid="pflege-ki-testen-page">
      {/* Skip-to-nav */}
      <header className="px-4 md:px-8 py-6 max-w-6xl mx-auto flex items-center justify-between">
        <Link to="/" className="text-[12px] uppercase tracking-[0.28em] font-semibold text-sand-500 hover:text-terra">
          ← Zurück
        </Link>
        <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-terra">DICKIES-CAMPUS · PFLEGE-KI</div>
      </header>

      {/* HERO */}
      <section className="px-4 md:px-8 max-w-6xl mx-auto pt-6 md:pt-14 pb-10">
        <div className="grid md:grid-cols-[1.15fr_1fr] gap-12 items-start">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-peach-100 border border-terra/30 px-4 py-1.5 text-[11px] uppercase tracking-[0.22em] font-bold text-terra mb-6">
              <Sparkles className="h-3.5 w-3.5" /> Schnupper-Woche · Nur für Pflegedienste
            </div>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-sand-800 leading-[1.05] tracking-tight">
              7 Tage Pflege-KI.<br/>
              <span className="text-terra">Für 29 € einmalig.</span>
            </h1>
            <p className="mt-6 text-[16px] md:text-[17px] text-sand-700 leading-relaxed max-w-xl">
              Testen Sie mit Ihrem Pflegedienst die komplette Pflege-KI: SIS, Maßnahmenplan,
              Pflegebericht, NBA-Gutachten und Höherstufungsbrief — inklusive
              deterministischem Halluzinationsschutz. <b>Kein Abo.</b> Endet nach 7 Tagen automatisch.
            </p>

            {/* Kontingent Box */}
            <div className="mt-8 rounded-2xl border-2 border-terra/40 bg-peach-50 p-6" data-testid="kontingent-box">
              <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-terra mb-3">Ihr Test-Kontingent · 7 Tage</div>
              <ul className="grid grid-cols-2 gap-y-2 gap-x-4 text-sand-800 text-[14.5px]">
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-terra shrink-0"/> 5 SIS</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-terra shrink-0"/> 5 Maßnahmenpläne</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-terra shrink-0"/> 3 NBA-Gutachten</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-terra shrink-0"/> 3 Höherstufungsbriefe</li>
                <li className="flex items-center gap-2 col-span-2 pt-1"><ShieldCheck className="h-4 w-4 text-terra shrink-0"/> Halluzinationsschutz &amp; Cross-Doc-Plausi inklusive</li>
              </ul>
            </div>

            {/* USPs */}
            <div className="mt-8 space-y-4 max-w-lg">
              <UspRow icon={Clock} title="3 Std. Doku pro Woche sparen" desc="Rechenbeispiel: 10 Kräfte × 4 Std./Woche = 40 Std. → mit Pflege-KI halbiert. ROI über 13×." />
              <UspRow icon={ShieldCheck} title="MD-Prüflogik" desc="Deterministische Prüfregeln überstimmen die KI, wenn sie erfindet. Cross-Doc-Plausi zwischen SIS, Maßnahmen &amp; Bericht." />
              <UspRow icon={FileText} title="Alles in einer Oberfläche" desc="Kundenakte · SIS · Maßnahmenplan · Pflegebericht · NBA · Höherstufung · Widerspruch — kein Tool-Wechsel." />
            </div>
          </div>

          {/* FORMULAR */}
          <form onSubmit={handleSubmit} className="rounded-2xl border-2 border-terra bg-white p-6 md:p-7 sticky top-8" data-testid="schnupper-form">
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-terra mb-1">Schnupper-Woche buchen</div>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-5xl font-display font-bold text-sand-800">29 €</span>
              <span className="text-sand-600 text-sm">einmalig · netto</span>
            </div>
            <div className="text-sand-500 text-[12px] mb-5">Kein Abo · endet nach 7 Tagen automatisch</div>

            <div className="space-y-3">
              <Field label="Pflegedienst · Firmenname *">
                <Input value={form.firmenname} onChange={(e)=>set('firmenname', e.target.value)} required data-testid="input-firmenname" placeholder="Musterpflege GmbH"/>
              </Field>
              <Field label="Ansprechpartner (PDL) *">
                <Input value={form.name} onChange={(e)=>set('name', e.target.value)} required data-testid="input-name" placeholder="Vor- und Nachname"/>
              </Field>
              <Field label="E-Mail *">
                <Input type="email" value={form.email} onChange={(e)=>set('email', e.target.value)} required data-testid="input-email" placeholder="pdl@ihre-pflegedienst.de"/>
              </Field>
              <Field label="Telefon *">
                <Input type="tel" value={form.phone} onChange={(e)=>set('phone', e.target.value)} required data-testid="input-phone" placeholder="0221 1234567"/>
              </Field>
              <Field label="Bundesland *">
                <select
                  value={form.bundesland}
                  onChange={(e)=>set('bundesland', e.target.value)}
                  required
                  data-testid="input-bundesland"
                  className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Bitte wählen</option>
                  {BUNDESLAND_OPTIONS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </Field>
              <Field label="Anzahl Pflegekräfte (optional)">
                <select
                  value={form.anzahl_mitarbeiter}
                  onChange={(e)=>set('anzahl_mitarbeiter', e.target.value)}
                  data-testid="input-anzahl"
                  className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Keine Angabe</option>
                  <option value="1-5">1–5</option>
                  <option value="6-15">6–15</option>
                  <option value="16-30">16–30</option>
                  <option value="31-50">31–50</option>
                  <option value="50+">50+</option>
                </select>
              </Field>
            </div>

            <Button
              type="submit"
              disabled={!canSubmit || busy}
              data-testid="submit-checkout"
              className="w-full mt-5 h-12 rounded-full bg-terra hover:bg-terra-dark text-white font-semibold text-[15px] inline-flex items-center justify-center gap-2"
            >
              {busy ? <><Loader2 className="h-4 w-4 animate-spin"/> Weiterleiten zur Kasse…</> : <>Jetzt für 29 € starten <ArrowRight className="h-4 w-4"/></>}
            </Button>

            <p className="text-[11.5px] text-sand-500 text-center mt-3 leading-snug">
              Zahlung sicher über Stripe · nach der Zahlung erhalten Sie umgehend Ihre Zugangsdaten per E-Mail.
              Nach 7 Tagen endet der Zugang automatisch — <b>kein Abo</b>. Weiter machen? Pflege-KI regulär 149 €/Monat, monatlich kündbar.
            </p>
          </form>
        </div>
      </section>

      {/* SO FUNKTIONIERT'S · 3 Steps */}
      <section className="px-4 md:px-8 max-w-6xl mx-auto pt-4 pb-16">
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-terra mb-3">So funktioniert&apos;s</div>
        <div className="grid md:grid-cols-3 gap-6 mt-4">
          {[
            { n: "01", t: "29 € bezahlen", d: "Formular ausfüllen, sichere Stripe-Zahlung, Zugangsdaten sofort per E-Mail." },
            { n: "02", t: "7 Tage arbeiten", d: "Loggen Sie sich ein und erstellen Sie echte SIS, Maßnahmenpläne, Gutachten für Ihre Klient:innen." },
            { n: "03", t: "Entscheiden", d: "Nach 7 Tagen endet der Zugang automatisch. Weiter machen? Ein Klick auf 149 €/Monat, monatlich kündbar." },
          ].map((s) => (
            <div key={s.n} className="rounded-2xl border border-cream-300 bg-white p-6">
              <div className="text-terra font-display text-3xl font-bold">{s.n}</div>
              <div className="mt-2 font-semibold text-sand-800 text-lg">{s.t}</div>
              <div className="mt-2 text-sand-600 text-[14px] leading-relaxed">{s.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ + Vertrauen */}
      <section className="px-4 md:px-8 max-w-4xl mx-auto pb-24">
        <h2 className="font-display text-2xl md:text-3xl text-sand-800 mb-6">Häufige Fragen</h2>
        <div className="space-y-4">
          <Faq q="Ist das ein Abo?" a="Nein. Sie zahlen 29 € einmalig, der Zugang endet nach exakt 7 Tagen automatisch. Ohne Kündigung, ohne Verlängerung."/>
          <Faq q="Was passiert nach 7 Tagen?" a="Der Zugang wird gesperrt und Sie erhalten eine E-Mail mit einem Upgrade-Angebot: Pflege-KI regulär 149 €/Monat, monatlich kündbar. Wenn Sie nicht upgraden, passiert nichts weiter."/>
          <Faq q="Für wen ist das gedacht?" a="Ambulante & stationäre Pflegedienste, die die Pflege-KI in Ruhe im echten Alltag testen wollen — nicht auf einer Demo-Website, sondern mit eigenen Klient:innen."/>
          <Faq q="Wie viele Nutzer können den Trial-Account nutzen?" a="Der Test-Account ist für eine Führungskraft (PDL) gedacht. Das Kontingent (5 SIS / 5 MP / 3 NBA / 3 Höherstufung) reicht, um sich einen realistischen Eindruck zu verschaffen."/>
          <Faq q="Ist die KI DSGVO-konform?" a="Ja. Alle Server stehen in Deutschland, die Verarbeitung ist DSGVO-konform. AVV auf Anfrage."/>
          <Faq q="Wie sicher ist die KI-Ausgabe?" a="Sehr sicher. Wir haben einen deterministischen Halluzinationsschutz gebaut, der die KI überstimmt, wenn sie Dinge erfindet. Zusätzlich Cross-Doc-Plausi zwischen SIS, Maßnahmen und Bericht."/>
        </div>
      </section>

      <footer className="border-t border-cream-300 px-4 md:px-8 py-8 text-sand-500 text-[13px]">
        <div className="max-w-6xl mx-auto flex flex-wrap justify-between gap-4">
          <div>© Dickies-Campus GmbH · dickies-helden.de</div>
          <div className="flex gap-4">
            <Link to="/agb" className="hover:text-terra">AGB</Link>
            <Link to="/datenschutz" className="hover:text-terra">Datenschutz</Link>
            <Link to="/impressum" className="hover:text-terra">Impressum</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[12px] font-semibold text-sand-700 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

function UspRow({ icon: Icon, title, desc }) {
  return (
    <div className="flex gap-3">
      <div className="shrink-0 h-9 w-9 rounded-full bg-peach-100 border border-terra/30 flex items-center justify-center">
        <Icon className="h-4 w-4 text-terra" strokeWidth={2}/>
      </div>
      <div>
        <div className="font-semibold text-sand-800 text-[15px]">{title}</div>
        <div className="text-sand-600 text-[13.5px] leading-relaxed">{desc}</div>
      </div>
    </div>
  );
}

function Faq({ q, a }) {
  return (
    <details className="rounded-xl border border-cream-300 bg-white p-4 group" data-testid="faq-item">
      <summary className="cursor-pointer font-semibold text-sand-800 text-[15px] list-none flex justify-between items-center">
        {q}
        <span className="text-terra text-xl group-open:rotate-45 transition-transform">+</span>
      </summary>
      <div className="mt-3 text-sand-600 text-[14px] leading-relaxed">{a}</div>
    </details>
  );
}


// ─────────────────────────────────────────────────────────────────
// SUCCESS PAGE (`/pflege-ki-testen/erfolg`)
// ─────────────────────────────────────────────────────────────────
export function PflegeKiTestenErfolg() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setState({ loading: false, error: "Keine Session-ID gefunden.", data: null });
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/schnupper/activate/${sessionId}`);
        if (cancelled) return;
        setState({ loading: false, error: null, data });
      } catch (err) {
        if (cancelled) return;
        if (attempts < 8) {
          setTimeout(poll, 1500);
        } else {
          setState({ loading: false, error: formatApiErrorDetail(err?.response?.data?.detail) || "Aktivierung fehlgeschlagen.", data: null });
        }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const copyPw = async (pw) => {
    try {
      await navigator.clipboard.writeText(pw);
      setCopied(true);
      toast.success("Passwort kopiert");
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  return (
    <div className="min-h-screen bg-cream-50" data-testid="pflege-ki-testen-erfolg">
      <header className="px-4 md:px-8 py-6 max-w-6xl mx-auto">
        <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-terra">DICKIES-CAMPUS · PFLEGE-KI</div>
      </header>

      <section className="px-4 md:px-8 max-w-2xl mx-auto pt-10 pb-20">
        {state.loading && (
          <div className="text-center py-24">
            <Loader2 className="h-10 w-10 animate-spin text-terra mx-auto mb-4"/>
            <p className="text-sand-700">Aktiviere Ihre Schnupper-Woche…</p>
          </div>
        )}

        {state.error && (
          <div className="rounded-2xl border border-red-300 bg-red-50 p-6 text-red-700" data-testid="error-box">
            <div className="font-semibold text-lg">Aktivierung fehlgeschlagen</div>
            <div className="text-sm mt-1">{state.error}</div>
            <div className="text-sm mt-4">Bitte kontaktieren Sie uns: <a href="mailto:info@dickies-helden.de" className="underline">info@dickies-helden.de</a></div>
          </div>
        )}

        {state.data && (
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-terra mb-2">Zahlung erfolgreich</div>
            <h1 className="font-display text-4xl md:text-5xl text-sand-800 leading-tight">Willkommen an Bord.</h1>
            <p className="mt-4 text-sand-700 text-[16px] leading-relaxed">
              Ihre Schnupper-Woche für <b>{state.data.firmenname || "Ihren Pflegedienst"}</b> ist aktiv.
              Wir haben Ihnen die Zugangsdaten auch per E-Mail gesendet.
            </p>

            {state.data.password_shown_once && state.data.password && (
              <div className="mt-8 rounded-2xl border-2 border-terra bg-white p-6" data-testid="credentials-box">
                <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-terra mb-3">Ihre Zugangsdaten</div>
                <div className="space-y-3 text-[15px]">
                  <div>
                    <div className="text-sand-500 text-[11px] uppercase tracking-wider mb-1">E-Mail</div>
                    <div className="font-mono text-sand-800" data-testid="cred-email">{state.data.email}</div>
                  </div>
                  <div>
                    <div className="text-sand-500 text-[11px] uppercase tracking-wider mb-1">Passwort</div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sand-800 bg-cream-100 px-3 py-1.5 rounded border border-cream-300" data-testid="cred-password">{state.data.password}</span>
                      <button
                        type="button"
                        onClick={() => copyPw(state.data.password)}
                        data-testid="copy-password-btn"
                        className="text-terra text-[13px] font-semibold inline-flex items-center gap-1 hover:underline"
                      >
                        <Copy className="h-3.5 w-3.5"/> {copied ? "Kopiert" : "Kopieren"}
                      </button>
                    </div>
                  </div>
                  <p className="text-[12px] text-red-600 pt-2">
                    ⚠ Dieses Passwort wird Ihnen nur ein einziges Mal angezeigt. Bitte notieren oder direkt kopieren.
                  </p>
                </div>
              </div>
            )}

            {state.data.already_active && (
              <div className="mt-8 rounded-xl border border-cream-300 bg-white p-5 text-sand-700 text-[14.5px]" data-testid="already-active-box">
                Diese Schnupper-Woche wurde bereits aktiviert. Wenn Sie Ihre Zugangsdaten
                verloren haben, nutzen Sie bitte &bdquo;Passwort vergessen&ldquo; auf der Login-Seite.
              </div>
            )}

            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                onClick={() => navigate("/campus/login")}
                data-testid="goto-login-btn"
                className="rounded-full bg-terra hover:bg-terra-dark text-white h-12 px-8 font-semibold text-[15px]"
              >
                Jetzt einloggen <ArrowRight className="h-4 w-4 ml-2"/>
              </Button>
              <Link to="/pflege-ki-testen" className="rounded-full border border-sand-300 text-sand-700 h-12 px-6 inline-flex items-center hover:bg-cream-100">
                Zur Landingpage
              </Link>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
